import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthCallback } from "@/components/AuthCallback";
import { AppLayout } from "@/components/AppLayout";
import { ConsentGate } from "@/components/ConsentGate";
import { CookieBanner } from "@/components/CookieBanner";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import PaymentResult from "@/pages/PaymentResult";
import Dashboard from "@/pages/app/Dashboard";
import Analyze from "@/pages/app/Analyze";
import Deadlines from "@/pages/app/Deadlines";
import Archive from "@/pages/app/Archive";
import Family from "@/pages/app/Family";
import Guides from "@/pages/app/Guides";
import GuideDetail from "@/pages/app/GuideDetail";
import Letters from "@/pages/app/Letters";
import Chat from "@/pages/app/Chat";
import Pricing from "@/pages/app/Pricing";
import Settings from "@/pages/app/Settings";
import Admin from "@/pages/app/Admin";
import Referral from "@/pages/app/Referral";
import FiscalRadar from "@/pages/app/FiscalRadar";
import ScamCheck from "@/pages/app/ScamCheck";
import CafNearby from "@/pages/app/CafNearby";
import Inbox, { Alerts } from "@/pages/app/Inbox";
import SimpleMode from "@/pages/SimpleMode";
import Auth from "@/pages/Auth";
import Demo from "@/pages/Demo";
import { VerifyEmail, ResetPassword } from "@/pages/AuthLinks";
import { useEffect } from "react";

const Loader = () => (
  <div className="min-h-screen grid place-items-center" data-testid="app-loader">
    <div className="h-8 w-8 rounded-full border-2 border-[#1E3A8A] border-t-transparent animate-spin" />
  </div>
);

const Protected = ({ children, admin }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/accedi" replace />;
  if (!user.consents) return <ConsentGate />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
};

const AppRouter = () => {
  const location = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) localStorage.setItem("bz_ref", ref.slice(0, 20));
  }, [location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/cookie" element={<Legal kind="cookie" />} />
      <Route path="/termini" element={<Legal kind="termini" />} />
      <Route path="/accedi" element={<Auth />} />
      <Route path="/prova" element={<Demo />} />
      <Route path="/verifica-email" element={<VerifyEmail />} />
      <Route path="/reimposta-password" element={<ResetPassword />} />
      <Route path="/payment/:result" element={<PaymentResult />} />
      <Route path="/semplice" element={<Protected><SimpleMode /></Protected>} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="analizza" element={<Analyze />} />
        <Route path="scadenze" element={<Deadlines />} />
        <Route path="archivio" element={<Archive />} />
        <Route path="famiglia" element={<Family />} />
        <Route path="guide" element={<Guides />} />
        <Route path="guide/:slug" element={<GuideDetail />} />
        <Route path="lettere" element={<Letters />} />
        <Route path="chat" element={<Chat />} />
        <Route path="pro" element={<Pricing />} />
        <Route path="invita" element={<Referral />} />
        <Route path="radar" element={<FiscalRadar />} />
        <Route path="truffe" element={<ScamCheck />} />
        <Route path="caf" element={<CafNearby />} />
        <Route path="email" element={<Inbox />} />
        <Route path="avvisi" element={<Alerts />} />
        <Route path="impostazioni" element={<Settings />} />
        <Route path="admin" element={<Protected admin><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
